import * as Sentry from "@sentry/nextjs";
import { logger } from "@portal/app/_common/lib/logger";

/**
 * Describes Payload CMS content that rendering code could not handle and had to
 * skip (an unknown question type, an unknown block type, ...).
 */
export interface CmsContentIssue {
  /** Where the content was being consumed, e.g. "FormFunnelPage". */
  source: string;
  /** Payload collection slug the content came from, e.g. "form-questions". */
  collection: string;
  /** What kind of mismatch this is, e.g. "unsupported_question_type". */
  issue: string;
  /** The value the code did not recognise, e.g. "multiselect". */
  value: string;
  /** Identifier of the offending document or field (fieldKey, block id, slug). */
  identifier?: string;
  /** Human-readable explanation, used as the log line and Sentry message. */
  message: string;
  extra?: Record<string, unknown>;
}

// One report per distinct issue per process (server) or page load (browser).
// Unhandled CMS content repeats on every render of the page that holds it, and
// a single Sentry issue per mismatch is the signal; thousands of duplicate
// events would only burn quota.
const reported = new Set<string>();

/**
 * Logs and reports CMS content the code could not handle to Sentry.
 *
 * Use this instead of a bare `logger.warn` wherever rendering code skips CMS
 * content it does not understand: pino output is not forwarded to Sentry, so a
 * warn-only mismatch can silently hide content from users for months.
 */
export const reportCmsContentIssue = (issue: CmsContentIssue): void => {
  const dedupeKey = [issue.source, issue.collection, issue.issue, issue.value, issue.identifier]
    .map((part) => part ?? "")
    .join("|");
  if (reported.has(dedupeKey)) return;
  reported.add(dedupeKey);

  logger.warn(
    {
      cmsSource: issue.source,
      cmsCollection: issue.collection,
      cmsIssue: issue.issue,
      cmsValue: issue.value,
      cmsIdentifier: issue.identifier,
      ...issue.extra,
    },
    issue.message
  );

  Sentry.captureMessage(issue.message, {
    level: "error",
    // Group by the mismatch itself so every page render lands on one issue.
    fingerprint: ["cms-content-issue", issue.collection, issue.issue, issue.value],
    tags: {
      "cms.source": issue.source,
      "cms.collection": issue.collection,
      "cms.issue": issue.issue,
      "cms.value": issue.value,
      ...(issue.identifier ? { "cms.identifier": issue.identifier } : {}),
    },
    extra: issue.extra,
  });
};

/** Test-only: clears the per-process dedupe set. */
export const resetReportedCmsContentIssuesForTests = (): void => {
  reported.clear();
};
